/**
 * Header bell "seen" watermark. Opening the panel clears the count.
 * It does not mark chats or userNotifications read.
 */

export const NOTIF_SEEN_PREFIX = 'm2b-notif-seen-at:';
export const NOTIF_HISTORY_LIMIT = 30;

export function notifSeenStorageKey(uid) {
  return `${NOTIF_SEEN_PREFIX}${uid}`;
}

export function itemActivityMs(item) {
  const t = item?.createdAt;
  if (!t) return 0;
  if (typeof t.toMillis === 'function') {
    const ms = t.toMillis();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof t.seconds === 'number') {
    return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  }
  if (typeof t === 'number' && Number.isFinite(t)) return t;
  if (t instanceof Date) {
    const ms = t.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  return 0;
}

/** Pending requests, unread chats, and unread bell types. Read rows never count. */
export function isAttentionNotification(item) {
  if (!item || item.status === 'read') return false;
  return (
    item.kind === 'friend_request' ||
    item.kind === 'chat_message' ||
    item.kind === 'memory_invite' ||
    item.kind === 'friend_accepted'
  );
}

/**
 * Missing timestamps count only before the bell has ever been opened.
 * A server timestamp that lands later still counts when it is newer than the watermark.
 */
export function isUnseenNotification(item, seenAtMs) {
  if (!isAttentionNotification(item)) return false;
  const ms = itemActivityMs(item);
  const seen = Number(seenAtMs) || 0;
  if (!ms) return seen <= 0;
  return ms > seen;
}

export function unseenCount(items, seenAtMs) {
  return (items || []).reduce(
    (n, item) => n + (isUnseenNotification(item, seenAtMs) ? 1 : 0),
    0
  );
}

export function readNotifSeenAt(uid) {
  if (!uid || typeof localStorage === 'undefined') return 0;
  try {
    const n = Number(localStorage.getItem(notifSeenStorageKey(uid)));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeNotifSeenAt(uid, ms) {
  if (!uid || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(notifSeenStorageKey(uid), String(ms));
  } catch {
    /* storage blocked — in-memory watermark still clears the badge */
  }
}

/** Persist a watermark at least as new as now and the newest attention row. */
export function markNotificationsSeen(uid, items, now = Date.now()) {
  if (!uid) return 0;
  let next = Math.max(readNotifSeenAt(uid), now);
  for (const item of items || []) {
    if (!isAttentionNotification(item)) continue;
    const ms = itemActivityMs(item);
    if (ms > next) next = ms;
  }
  writeNotifSeenAt(uid, next);
  return next;
}

/**
 * Newest-first list. Attention rows always stay; quiet history fills the rest up to `limit`.
 */
export function capNotificationHistory(items, limit = NOTIF_HISTORY_LIMIT) {
  const list = items || [];
  const attentionCount = list.filter(isAttentionNotification).length;
  const historyRoom = Math.max(0, limit - attentionCount);
  let historyKept = 0;
  const out = [];
  for (const item of list) {
    if (isAttentionNotification(item)) {
      out.push(item);
      continue;
    }
    if (historyKept < historyRoom) {
      out.push(item);
      historyKept += 1;
    }
  }
  return out;
}
